import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseStorage';

export interface AppBoardEntry {
  app_id: string;
  app_name: string;
  counts: number;
  most_used_by_id: string;
  most_used_by_name: string;
  most_used_in_country: string;
  route_paths: string[];
}

export const useAppBoard = () => {
  const [data, setData] = useState<AppBoardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAppBoard = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: appBoardData, error: fetchError } = await supabase
        .from('app_board')
        .select('*')
        .order('counts', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // For each app_id, fetch corresponding route_paths from pages table and use first one as app_name
      const enrichedData = await Promise.all(
        (appBoardData || []).map(async (app) => {
          const { data: pages, error: pagesError } = await supabase
            .from('pages')
            .select('route_path')
            .eq('app_id', app.app_id);

          if (pagesError) {
            console.error('Pages fetch error for app_id:', app.app_id, pagesError);
          }

          const routePaths = pages?.map(page => page.route_path) || [];
          const appName = routePaths.length > 0 ? routePaths[0] : app.app_id;

          return {
            ...app,
            app_name: appName,
            route_paths: routePaths
          };
        })
      );

      setData(enrichedData || []);
    } catch (err) {
      console.error('Error fetching app board data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch app board data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppBoard();
  }, []);

  return { data, loading, error, refetch: fetchAppBoard };
};